import React, { useState, useRef } from 'react';
import { Box, Typography, IconButton, Slider, Collapse } from '@mui/material';
import { Headphones, Play, Pause, ChevronDown, FileText } from 'lucide-react';

const HEADING = '#fafafa';
const SECONDARY = '#a1a1aa';
const MUTED = '#71717a';
const ACCENT = '#a78bfa';
const CARD_BDR = '1px solid rgba(255,255,255,0.08)';
const CARD_RAD = '14px';

const formatTime = (secs) => {
  if (!secs || Number.isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioLessonPlayer = ({ audioUrl, transcript, title }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);

  if (!audioUrl) return null;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else el.play().catch(() => {});
    setPlaying(!playing);
  };

  return (
    <Box sx={{ mb: 3, borderRadius: CARD_RAD, overflow: 'hidden', border: CARD_BDR, bgcolor: 'rgba(255,255,255,0.02)' }}>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}
      />

      <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
          bgcolor: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Headphones size={20} color={ACCENT} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: HEADING, fontWeight: 700, fontSize: 14 }}>
            {title || 'Audio Lesson'}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 11 }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>
          <Slider
            size="small"
            value={currentTime}
            max={duration || 100}
            onChange={(_, v) => {
              if (audioRef.current) audioRef.current.currentTime = v;
              setCurrentTime(v);
            }}
            sx={{
              color: ACCENT, py: 0.5,
              '& .MuiSlider-rail': { opacity: 0.2 },
              '& .MuiSlider-thumb': { width: 12, height: 12 },
            }}
          />
        </Box>

        <IconButton onClick={togglePlay} sx={{ color: HEADING, bgcolor: 'rgba(167,139,250,0.15)', '&:hover': { bgcolor: 'rgba(167,139,250,0.25)' } }}>
          {playing ? <Pause size={20} /> : <Play size={20} fill={HEADING} />}
        </IconButton>
      </Box>

      {transcript && (
        <>
          <Box
            component="button"
            onClick={() => setShowTranscript((s) => !s)}
            sx={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 1,
              px: 2.5, py: 1.25, border: 'none', borderTop: CARD_BDR, cursor: 'pointer',
              bgcolor: 'transparent', textAlign: 'left',
            }}
          >
            <FileText size={14} color={SECONDARY} />
            <Typography sx={{ color: SECONDARY, fontSize: 12, fontWeight: 600, flex: 1 }}>
              Transcript
            </Typography>
            <ChevronDown size={14} color={MUTED} style={{ transform: showTranscript ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </Box>
          <Collapse in={showTranscript}>
            <Typography sx={{ px: 2.5, pb: 2, color: MUTED, fontSize: 13, lineHeight: 1.7 }}>
              {transcript}
            </Typography>
          </Collapse>
        </>
      )}
    </Box>
  );
};

export default AudioLessonPlayer;